import * as React from 'react';
import * as fp from 'lodash/fp';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  MigrationState,
  Workspace,
  WorkspaceAccessLevel,
} from 'generated/fetch';

import { environment } from 'environments/environment';
import { StyledRouterLink } from 'app/components/buttons';
import { WorkspaceCardBase } from 'app/components/card';
import { ConfirmWorkspaceDeleteModal } from 'app/components/confirm-workspace-delete-modal';
import { FlexColumn, FlexRow } from 'app/components/flex';
import {
  ClrIcon,
  CommunityIcon,
  ControlledTierBadge,
} from 'app/components/icons';
import { MigrationBadge } from 'app/components/migration/migration-badge';
import { RecoveryBadge } from 'app/components/migration/recovery-badge';
import { withErrorModal } from 'app/components/modals';
import { TooltipTrigger } from 'app/components/popups';
import { dataTabPath } from 'app/routing/utils';
import { workspacesApi } from 'app/services/swagger-fetch-clients';
import colors, { colorWithWhiteness } from 'app/styles/colors';
import { reactStyles } from 'app/utils';
import {
  AccessTierShortNames,
  displayNameForTier,
} from 'app/utils/access-tiers';
import { AnalyticsTracker, triggerEvent } from 'app/utils/analytics';
import { displayDate } from 'app/utils/dates';
import { NavigationProps } from 'app/utils/navigation';
import { notificationStore } from 'app/utils/stores';
import { withNavigation } from 'app/utils/with-navigation-hoc';
import { isCommunityWorkspace } from 'app/utils/workspace-utils';

const EVENT_CATEGORY = 'Workspace list';

const styles = reactStyles({
  workspaceCard: {
    justifyContent: 'flex-end',
    height: '100%',
    // Set relative positioning so the spinner overlay is centered in the card.
    position: 'relative',
  },
  workspaceMenuWrapper: {
    paddingTop: '.75rem',
    borderRight: '1px solid',
    borderColor: colorWithWhiteness(colors.dark, 0.6),
    flex: '0 0 1.5rem',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  workspaceName: {
    color: colors.accent,
    marginBottom: '0.75rem',
    fontSize: 18,
    wordBreak: 'break-all',
  },
  workspaceNameDisabled: {
    color: colors.disabled,
    marginBottom: '0.75rem',
    fontSize: 18,
    wordBreak: 'break-all',
    pointerEvents: 'none',
    cursor: 'not-allowed',
  },
  permissionBox: {
    color: colors.white,
    height: '1.5rem',
    width: '4.5rem',
    fontSize: 10,
    textAlign: 'center',
    borderRadius: '0.3rem',
    padding: 0,
  },
  deleteBadge: {
    height: '1.5rem',
    minWidth: '6.3rem',
    padding: '0 8px',
    borderRadius: '0.3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '.3px',
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
    cursor: 'pointer',
    border: '1px solid #FCA5A5',
  },
  lockWorkspace: {
    color: colors.warning,
    marginBottom: '0.15rem',
    width: '21px',
    height: '21px',
    viewBox: '0 0 25 27',
  },
  workspaceMenuWrapperDisabled: {
    paddingTop: '.75rem',
    borderRight: '1px solid',
    borderColor: colorWithWhiteness(colors.dark, 0.3),
    flex: '0 0 1.5rem',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: colors.light,
  },
});

interface WorkspaceCardState {
  confirmDeleting: boolean;
}

interface WorkspaceCardProps extends NavigationProps {
  workspace: Workspace;
  accessLevel: WorkspaceAccessLevel;
  // A reload function that can be called by this component to request a refresh
  // of the workspace contained in this card.
  reload(): Promise<void>;
  // non-CT users cannot click or see on CT workspaces.
  tierAccessDisabled?: boolean;
  useFeaturedWorkspacePageUi?: boolean;
  isMigratedView?: boolean;
  // Show a dedicated delete icon for migrated RW1.0 cards.
  showDeleteAction?: boolean;
  // Caller can override who is allowed to use the delete icon.
  canDeleteAction?: boolean;
  // Prevent navigating into the workspace from the card.
  disableOpenAction?: boolean;
}

export const WorkspaceCard = fp.flow(withNavigation)(
  class extends React.Component<WorkspaceCardProps, WorkspaceCardState> {
    constructor(props) {
      super(props);
      this.state = {
        confirmDeleting: false,
      };
    }

    deleteWorkspace = withErrorModal(
      {
        title: 'Error Deleting Workspace',
        message: `Could not delete workspace '${this.props.workspace.name}'.`,
        showBugReportLink: true,
        onDismiss: () => {
          this.setState({ confirmDeleting: false });
        },
      },
      async () => {
        AnalyticsTracker.Workspaces.Delete();
        await workspacesApi().deleteWorkspace(
          this.props.workspace.namespace,
          this.props.workspace.terraName
        );
        await this.props.reload();
        notificationStore.set({
          title: 'Workspace deleted',
          message: `Workspace '${this.props.workspace.name}' was deleted.`,
        });
        this.setState({ confirmDeleting: false });
      }
    );

    trackWorkspaceNavigation() {
      const {
        workspace: { name, featuredCategory },
      } = this.props;
      !!featuredCategory
        ? AnalyticsTracker.Workspaces.NavigateToFeatured(name)
        : triggerEvent(EVENT_CATEGORY, 'navigate', 'Click on workspace name');
    }

    render() {
      const {
        workspace,
        workspace: { accessTierShortName, adminLocked, namespace, terraName },
        accessLevel,
        tierAccessDisabled,
        useFeaturedWorkspacePageUi,
        isMigratedView,
        showDeleteAction = false,
        canDeleteAction,
        disableOpenAction = false,
      } = this.props;
      const { confirmDeleting } = this.state;
      const isArchived = workspace.recoveryState != null;

      const canUseDeleteAction =
        canDeleteAction ?? accessLevel === WorkspaceAccessLevel.OWNER;
      const isDeleted = terraName == null;

      const getWorkspacePath = () => {
        if (workspace.recoveryState === 'NOT_STARTED') {
          return `/workspaces/${namespace}/recovery`;
        }

        return dataTabPath(namespace, terraName);
      };
      return (
        <React.Fragment>
          <WorkspaceCardBase>
            <FlexRow style={{ height: '100%' }}>
              <FlexColumn
                style={{
                  ...styles.workspaceCard,
                  padding: '.75rem',
                }}
                data-test-id='workspace-card'
              >
                <FlexColumn style={{ marginBottom: 'auto' }}>
                  <FlexColumn>
                    {!disableOpenAction ? (
                      <StyledRouterLink
                        style={
                          tierAccessDisabled
                            ? styles.workspaceNameDisabled
                            : styles.workspaceName
                        }
                        analyticsFn={() => this.trackWorkspaceNavigation()}
                        data-test-id={'workspace-card-link'}
                        propagateDataTestId
                        path={getWorkspacePath()}
                      >
                        <TooltipTrigger
                          content={
                            tierAccessDisabled && (
                              <div>
                                This workspace is a{' '}
                                {displayNameForTier(accessTierShortName)}{' '}
                                workspace. You do not have access. Please
                                complete the data access requirements to gain
                                access.
                              </div>
                            )
                          }
                        >
                          <div
                            style={
                              tierAccessDisabled
                                ? styles.workspaceNameDisabled
                                : styles.workspaceName
                            }
                            data-test-id='workspace-card-name'
                          >
                            {workspace.name}
                          </div>
                        </TooltipTrigger>
                      </StyledRouterLink>
                    ) : (
                      <TooltipTrigger
                        content={
                          'This migrated Legacy Workbench workspace can no longer be opened. ' +
                          'Use the matching Verily Workbench workspace instead.'
                        }
                      >
                        <div
                          style={styles.workspaceNameDisabled}
                          data-test-id='workspace-card-name'
                        >
                          {workspace.name}
                        </div>
                      </TooltipTrigger>
                    )}

                    {(workspace.migrationState === 'FINISHED' ||
                      workspace.recoveryState === 'RECOVERED') &&
                      isMigratedView && (
                        <a
                          href={`${environment.vwbUiUrl}/workspaces/${workspace.namespace}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{
                            fontSize: '12px',
                            color: colors.accent,
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginTop: '2px',
                          }}
                        >
                          Open in Verily Workbench
                          <ClrIcon shape='pop-out' size={10} />
                        </a>
                      )}
                  </FlexColumn>
                  {workspace.researchPurpose.reviewRequested === true &&
                    workspace.researchPurpose.approved === false && (
                      <div style={{ color: colors.danger }}>
                        <ClrIcon
                          shape='exclamation-triangle'
                          className='is-solid'
                          style={{ fill: colors.danger }}
                        />
                        Rejected
                      </div>
                    )}
                </FlexColumn>
                <FlexRow style={{ justifyContent: 'space-between' }}>
                  <FlexColumn>
                    <FlexRow
                      style={{
                        gap: '6px',
                        rowGap: '6px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div
                        data-test-id='workspace-access-level'
                        style={{
                          ...styles.permissionBox,
                          backgroundColor:
                            colors.workspacePermissionsHighlights[accessLevel],
                        }}
                      >
                        {accessLevel}
                      </div>

                      {isArchived ||
                      (workspace.migrationState !== MigrationState.FINISHED &&
                        typeof workspace.recoveryState === 'undefined') ? (
                        <RecoveryBadge state={workspace.recoveryState} />
                      ) : (
                        workspace.migrationState && (
                          <MigrationBadge state={workspace.migrationState} />
                        )
                      )}

                      {showDeleteAction &&
                        canUseDeleteAction &&
                        !adminLocked && (
                          <TooltipTrigger
                            side='top'
                            content={
                              'Delete this Legacy Workbench workspace. Your RW 2.0 workspace will not ' +
                              'be affected. Deleting will stop incurring charges for unused legacy resources.'
                            }
                            disabled={isDeleted}
                          >
                            <div
                              role='button'
                              aria-label='Delete workspace'
                              data-test-id='delete-migrated-workspace'
                              style={{
                                ...styles.deleteBadge,
                                ...(isDeleted
                                  ? { opacity: 0.4, cursor: 'not-allowed' }
                                  : {}),
                              }}
                              onClick={() => {
                                if (!isDeleted) {
                                  AnalyticsTracker.Workspaces.OpenDeleteModal(
                                    'Card'
                                  );
                                  triggerEvent(
                                    EVENT_CATEGORY,
                                    'delete',
                                    'Migrated workspace - click delete badge'
                                  );
                                  this.setState({ confirmDeleting: true });
                                }
                              }}
                            >
                              DELETE{isDeleted && 'D'}
                            </div>
                          </TooltipTrigger>
                        )}
                    </FlexRow>
                    <div style={{ fontSize: 12 }}>
                      Last Changed: {displayDate(workspace.lastModifiedTime)}
                    </div>
                    {useFeaturedWorkspacePageUi && (
                      <div style={{ fontSize: 12 }}>
                        Created By:{' '}
                        {workspace.creatorUser.userName.split('@')[0]}
                      </div>
                    )}
                  </FlexColumn>
                  <FlexColumn
                    style={{ justifyContent: 'flex-end', marginLeft: '1.2rem' }}
                  >
                    <FlexRow style={{ alignContent: 'space-between' }}>
                      {accessTierShortName ===
                        AccessTierShortNames.Controlled && (
                        <ControlledTierBadge />
                      )}
                      {!useFeaturedWorkspacePageUi &&
                        isCommunityWorkspace(workspace) && (
                          <FlexColumn
                            aria-label='Community Workspace'
                            style={{
                              justifyContent: 'flex-end',
                              marginLeft: '0.5rem',
                              marginRight: '0.5rem',
                            }}
                          >
                            <TooltipTrigger content='Workspace is published as Community Workspace'>
                              {/* Keeping the style consistent with Controlled Tier Badge*/}
                              <CommunityIcon />
                            </TooltipTrigger>
                          </FlexColumn>
                        )}
                      {adminLocked && (
                        <FlexColumn
                          data-test-id='workspace-lock'
                          style={{ justifyContent: 'flex-end' }}
                        >
                          <TooltipTrigger content='Workspace compliance action is required'>
                            <FontAwesomeIcon
                              icon={faLock}
                              style={styles.lockWorkspace}
                            />
                          </TooltipTrigger>
                        </FlexColumn>
                      )}
                    </FlexRow>
                  </FlexColumn>
                </FlexRow>
              </FlexColumn>
            </FlexRow>
          </WorkspaceCardBase>
          {confirmDeleting && (
            <ConfirmWorkspaceDeleteModal
              data-test-id='confirm-delete-modal'
              closeFunction={() => {
                this.setState({ confirmDeleting: false });
              }}
              receiveDelete={() => {
                AnalyticsTracker.Workspaces.Delete();
                this.deleteWorkspace();
              }}
              workspaceName={workspace.name}
            />
          )}
        </React.Fragment>
      );
    }
  }
);
